import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * One collapsible block on the driver's screen.
 *
 * The execution page is used one-handed, on a phone, often standing at a gate,
 * and it had four fully-expanded cards stacked down it: the run, the box's
 * details as five open text inputs, the packing list as a row per item, and the
 * documents. Everything was visible, which sounds like a virtue and is not —
 * the one thing a driver opens the screen to do is confirm the next step, and
 * it was reachable only by scrolling past the things they were not doing.
 *
 * So everything that is *reference* collapses, and everything that is *the job*
 * does not. A collapsed block still answers its own headline question in the
 * summary beside its title — how many documents, what the cargo weighs, which
 * container — so closing it costs no information, only the detail. Opening is
 * one tap and the block stays open for the rest of the visit.
 *
 * `defaultOpen` is a prop rather than a constant because the answer changes with
 * the container: the details block opens by itself when the run is blocked on
 * something the driver has to type into it, which is exactly when it stops
 * being reference and becomes the job.
 */
interface DriverSectionProps {
  title: string;
  /** What the block says while shut — the headline answer, not a teaser. */
  summary?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  defaultOpen?: boolean;
  /** Drawn on the title row whether the block is open or shut. */
  badge?: React.ReactNode;
  children: React.ReactNode;
}

export function DriverSection({
  title,
  summary,
  icon: Icon,
  defaultOpen = false,
  badge,
  children,
}: DriverSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="overflow-hidden rounded-2xl border border-[#E4E2DA] bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((state) => !state)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        {Icon && <Icon className="h-4 w-4 shrink-0 text-[#A6A49C]" />}

        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold uppercase tracking-wide text-[#A6A49C]">
            {title}
          </span>
          {/* Only while shut: open, the block itself is the answer, and a
              summary repeating it is a line of noise on a small screen. */}
          {!open && summary && (
            <span className="mt-0.5 block truncate text-[13px] text-[#1A1A18]">
              {summary}
            </span>
          )}
        </span>

        {badge}

        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-[#A6A49C]" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-[#A6A49C]" />
        )}
      </button>

      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}
